/* Import External Modules */
import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import Pagination from 'react-js-pagination'
import classnames from 'classnames'
import { Button } from 'react-bootstrap';
import { Text, Label } from '@leverege/ui-elements'

/* Import Local Modules */
import { TemplateActions, BreadcrumbActions, NotifyActions, Constants } from '~/actions'
import { List, Screen, TextInput, LabelGroup, Loading } from '~/views'
import PaginationSearch from '../utils/PaginationSearch'
import TemplateItem from './TemplateItem'
import Templates from './Templates'
import TemplateSelector from './TemplateSelector'

/* Import Styles */
import S from './TemplatesScreen.css'

/**
 * Templates Screen Component.
 */
class TemplatesScreen extends React.Component {

  /* Initialize Prop Types */
  static propTypes = {
    project : PropTypes.object
  }

  /**
   * Constructs instance of templates screen.
   * @param {Object} props - Props object. TODO: Expand.
   * @param {Object} context - Context object. TODO: Expand.
   */
  constructor( props, context ) {
    super( props, context )

    // Initialize pager
    this.pager = new PaginationSearch( this, 'templates', ( props, opts ) => {
      this.props.dispatch( TemplateActions.list( this.props.match.params.projectId, opts ) )
    } )

    // Initialize state
    this.state = { 
      selectId : null, 
      start : this.pager.getStartFrom( props ), 
      search : this.pager.getSearchFrom( props ),
      showAddDialog : false,
    }
  }

  /**
   * Initializes pager and breadcrumbs after component mounts.
   */
  componentDidMount() {
    this.pager.loadPage( this.props )
    this.props.dispatch( BreadcrumbActions.setBreadcrumbs( [ { name : 'templates' } ] ) )
  }

  /**
   * Updates pager when component will receive props.
   */
  componentWillReceiveProps( next ) {
    this.pager.loadIfNeeded( next )
  }

  /**
   * Handles events.
   * @param {*} evt - TODO.
   * @param {*} item 
   */
  onEvent = ( evt, item ) => {
    const { params } = this.props.match
    switch ( evt ) {
      case TemplateItem.REMOVE_EVENT:
        this.props.dispatch( TemplateActions.delete(
          params.projectId,
          item
        ) )
        break
      case TemplateItem.SELECTED:
        if ( item && item.id ) {
          this.props.history.push( `/project/${this.props.match.params.projectId}/template/${item.id}` )
        }
        break
      default:
    }
  }

  getTemplateContext = () => {
    const moltenUi = process.env.MOLTEN_UI || 'https://molten-ui.web.app'

    return {
      moltenUi
    }
  }
  
  /**
   * Handles template creation.
   */
  onCreateTemplate = async ( event ) => {

    // Get parameters
    const { dispatch, match, templates } = this.props
    const projectId = match?.params?.projectId
    const { alias, name } = event?.data || {}

    // Validate parameters
    if ( alias === null || name == null || projectId == null ) {
      console.log( 'Invalid template parameters' )
      return
    }
    
    // Get template
    const t = Templates.find( t => t.alias === alias && t.name === name )
    
    // Initialize template
    const template = buildTemplateRequest( t, this.getTemplateContext() )

    // Create template
    await dispatch( TemplateActions.create( projectId, template ) )
    
    // Hide dialog
    this.setState( { showAddDialog : false } )
  }

  /**
   * Handles template changes.
   */
  onTemplateChange = ( { path, target, value } ) => {
    // console.log( path, target, value )
  }

  /**
   * Handles template errors.
   * @param {} path - TODO.
   * @param {*} value 
   * @param {*} err 
   */
  onTemplateError = ( path, value, err ) => {
    this.props.dispatch( NotifyActions.error( err.message, value, path ) )
  }

  /**
   * Renders component.
   */
  render() {

    // Get parameters
    const { selectId, search, showAddDialog } = this.state
    const { templates } = this.props
    const { params } = this.props.match
    const page = this.pager.getPage()
    const total = this.pager.getTotal()
    const perPage = this.pager.getPerPage()
    const projectId = params && params.projectId
    const isSelected = ( item ) => {
      return selectId === ( item && item.id )
    }
    const loading = templates.status == Constants.STATUS_LOADING
    const items = templates.projectId == projectId ? this.pager.getPageItems() : []
    let selectItem = null
    items.forEach( ( item ) => {
      if ( selectId === item.id ) selectItem = item
    } )

    // Initialize left
    const left = (
      <div className={S.left}>
        <List className={classnames( S.list, S.splitLeft )}
          onEvent={this.onEvent} 
          isSelected={isSelected}
          noItemsView={loading ? null : null /* TODO: put something here */}
          items={items}
          itemView={TemplateItem}
          itemProps={{ hideNavButtons : false }} />
        <div className={S.pagination}>
          <Pagination
            innerClass={classnames( 'pagination', S.innerPagination )}
            activePage={page}
            itemsCountPerPage={perPage}
            totalItemsCount={total}
            pageRangeDisplayed={5}
            onChange={this.pager.onPageChange} />
        </div>
        <Loading show={loading} message="Loading..." />
      </div>
    )

    // Return screen component
    return (
      <Screen className={S.screen}>
        
        {/* Screen Header */}
        <Screen.Header className={S.header}>

          {/* Title */}
          <Text variant="screenTitle">Templates</Text>

          {/* Search Bar */}
          <div className={S.flexRight} style={{ display : 'inline-box', whiteSpace : 'nowrap' }}>
            <LabelGroup style={{ display : 'inline-flex', marginRight : 20 }}>
              <Label><i className="fa fa-search" /></Label>
              <TextInput value={search}
                hint="search" 
                changeOnBlur={false}
                changeOnReturn
                onChange={this.pager.onSearchChange} />
            </LabelGroup>

            {/* Add Button */}
            <Button bsStyle="primary"
              className="add-button" 
              onClick={() => this.setState( { showAddDialog : true } )}>
              <i className="fa fa-fw fa-circle-plus fa-lg" /> New Template 
            </Button>
          </div>
        </Screen.Header>

        {/* Screen Content */}
        <Screen.Content>
          {left}
        </Screen.Content>

        {/* Popups */}
        <TemplateSelector 
          templates={Templates}
          userTemplates={templates}
          show={showAddDialog}
          onClick={this.onCreateTemplate}
          onClose={() => { this.setState( { showAddDialog : false } ) }}/>
      </Screen>
    )
  }
}

function buildTemplateRequest( tBuilder, cxt ) {
  const { name, description, alias, ...rest } = tBuilder || {}

  const intents = {
    ...Object.keys( rest ).reduce( ( prev, key ) => {
      prev[key] = {}
      const { subject, data, dataType = 'hbs' } = rest[key]

      if ( subject ) {
        prev[key].subject = typeof subject === 'function' ? subject( cxt ) : subject
      }

      if ( data ) {
        prev[key].data = typeof data === 'function' ? data( cxt ) : data
      }

      prev[key].dataType = dataType

      return prev
    }, {} )
  }

  return {
    alias,
    name,
    description,
    intents : Object.keys( intents ).length > 0 ? intents : undefined
  } 
}

/* Export and Connect Module */
export default connect(
  state => ( {
    templates : state.templates,
    projects : state.projects
  } )
)( TemplatesScreen )
